import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import signature from '../../../assets/signiture.png';
import { environment } from '../../../environments/environment';

const SectionContainer = styled(Box)(({ theme }) => ({
  width: '80%',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0),
  },
}));

const ContentGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(6),
  alignItems: 'center',
  margin: ' auto',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 2),
    gap: theme.spacing(3),
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  overflow: 'hidden',
  borderRadius: '20px',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  [theme.breakpoints.down('lg')]: {
    aspectRatio: '4 / 3',
  },
  [theme.breakpoints.down('md')]: {
    aspectRatio: '16 / 9',
    order: -1,
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    aspectRatio: '4 / 3',
    borderRadius: '15px',
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
}));

const SectionTag = styled(Typography)(({ theme }) => ({
  color: environment.mainColor,
  fontSize: '17px',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  textTransform: 'capitalize',
  letterSpacing: '1px',
  fontFamily: '"Oswald", sans-serif',

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    marginBottom: theme.spacing(1),
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '60px',
  fontWeight: 700,
  lineHeight: '1.2',
  color: '#212121',
  letterSpacing: '0%',
  marginBottom: theme.spacing(3),
  fontFamily: '"Oswald", sans-serif',
  textTransform: 'capitalize',
  [theme.breakpoints.down('lg')]: {
    fontSize: '50px',
    lineHeight: '1.3',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
    lineHeight: '1.4',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    lineHeight: '1.5',
    marginBottom: theme.spacing(2),
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '28px',
  color: '#5C5C5B',
  marginBottom: theme.spacing(4),
  fontFamily: '"poppins", sans-serif',
  letters: '0%',

  [theme.breakpoints.down('md')]: {
    fontSize: '14px',

    margin: '0 auto',
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    marginBottom: theme.spacing(3),
    width: '100%',
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  position: 'relative',
  paddingRight: theme.spacing(3),

  '&::before': {
    content: 'attr(data-letter)',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '120px',
    fontWeight: 700,
    color: '#F5F5F5',
    zIndex: -1,
    fontFamily: '"Oswald", sans-serif',
    opacity: 0.5,
  },

  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: '60px',
    backgroundColor: '#E0E0E0',
    zIndex: 1,
  },

  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    paddingRight: 0,

    '&::before': {
      fontSize: '80px',
    },

    '&:not(:last-child)::after': {
      display: 'none',
    },

    '&:not(:last-child)': {
      borderBottom: '1px solid #E0E0E0',
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },

  [theme.breakpoints.down('sm')]: {
    '&::before': {
      fontSize: '60px',
    },
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontSize: '50px',
  fontWeight: 700,
  color: environment.mainColor,
  lineHeight: '56px',
  fontFamily: '"Oswald", sans-serif',
  position: 'relative',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 500,
  color: '#212121',
  textTransform: 'capitalize',
  letterSpacing: '0.5px',
  marginTop: theme.spacing(0.5),
  fontFamily: '"Oswald", sans-serif',
  position: 'relative',
  zIndex: 2,
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}));

const FoundationBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#212121',
  color: 'white',
  padding: theme.spacing(1, 2),
  borderRadius: '19px',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: '"Oswald", sans-serif',
  minWidth: '339px',
  height: '47px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    padding: theme.spacing(0.8, 1.5),
    minWidth: '250px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: theme.spacing(0.8, 1.5),
    minWidth: '200px',
  },
}));

const AboutFoodSection = ({
  foodImage,
  sectionTag = 'About Our Food',
  title = 'The Best Delicious Food Made From Us...',
  description = 'Its the perfect dining experience where every dish is crafted with fresh, high-quality dining experience quick and efficient service that ensures your food is servead fresh Its where every dining experience where every dish is crafted with fresh, high-quality ingredients fresh, high-quality',
  stats = [
    { number: '250+', label: 'Satisfied Clients' },
    { number: '153+', label: 'Total Food Categories' },
    { number: '25+', label: 'Award Win' },
  ],
  foundationText = 'Foundation, Since 21st Oct, 2019',
}) => {
  return (
    <SectionContainer>
      <ContentGrid>
        <ImageContainer>
          {foodImage ? (
            <img src={foodImage} alt="Delicious food" />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6c757d',
                fontSize: '16px',
              }}
            >
              Food Image Placeholder
            </Box>
          )}
        </ImageContainer>
        <ContentContainer>
          <SectionTag>{sectionTag}</SectionTag>
          <MainTitle>{title}</MainTitle>
          <Description>{description}</Description>
          {/* <StatsContainer>
            {stats.map((stat, index) => {
              const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
              return (
                <StatItem key={index} data-letter={letters[index]}>
                  <StatNumber>{stat.number}</StatNumber>
                  <StatLabel>{stat.label}</StatLabel>
                </StatItem>
              );
            })}
          </StatsContainer> */}
          {/* <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 2 }}
            alignItems="center"
            sx={{
              '& img': {
                width: { xs: '150px', sm: '180px', md: '200px' },
                height: 'auto',
              },
            }}
          >
            <img src={signature} alt="signature" style={{ width: '200px' }} />
            <FoundationBadge>
              Foundation, Since{' '}
              <span
                style={{ color: environment.mainColor, fontWeight: 'bold' }}
              >
                21st
              </span>{' '}
              Oct, 2019
            </FoundationBadge>
          </Stack> */}
        </ContentContainer>
      </ContentGrid>
    </SectionContainer>
  );
};

export default AboutFoodSection;
